import React, { useEffect, useState } from 'react';
import {
  useHistory, useLocation, useParams, useRouteMatch, Switch, Route, Redirect,
} from 'react-router-dom';

import { AccountIntent, ActivationProvider, useActivation } from './ActivationContext';
import { AuthMethodSelection } from './steps/AuthMethodSelection';
import { OtpVerification } from './steps/OtpVerification';
import { CoverageForm } from './steps/CoverageForm';
import { ActivationComplete } from './steps/ActivationComplete';
import { EmailEntry } from './steps/EmailEntry';
import { EmailSent } from './steps/EmailSent';
import { EmailCallback } from './steps/EmailCallback';

import { getSupabaseClient } from '@/utils/supabaseClient';
import { buildRedirectParam, isSafeRedirectUri } from '@/utils/urlHelpers';
import { activateSleepioConfirm } from '@/utils/sleepioActivationApi';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';

interface RouteParams {
  lob: string;
  // eslint-disable-next-line camelcase
  org_slug: string;
}

// Placeholder rendered for steps not yet implemented in this sprint.
const NotYetImplemented: React.FC<{ step: string; ticket?: string }> = ({ step, ticket = 'DAYL-1125' }) => (
  <div style={{ padding: 32, fontFamily: 'monospace' }}>
    <strong>
      [
      {ticket}
      {' '}
      scaffold]
    </strong>
    {' '}
    Step
    {' '}
    <code>{step}</code>
    {' '}
    not yet implemented.
  </div>
);

const ActivationRouterInner: React.FC = () => {
  // path = route pattern with :param placeholders — used in <Route path>
  // url  = actual matched URL with values filled in — used in <Redirect to>
  const { path, url } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const { state, dispatch } = useActivation();
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // platform stays as a query param — it's OS context, not intent (see DAYL-1137)
    const platform = params.get('platform');
    if (platform === 'native') {
      dispatch({ type: 'SET_PLATFORM', platform: 'native' });
    }
    // Default is 'web', already set in initial state — no action needed for absent/unknown values

    // Thread the redirect target through context (for same-tab navigation) and
    // sessionStorage (survives the email magic link tab hop back to /callback).
    const redirectTo = params.get('redirect_to');
    if (redirectTo && isSafeRedirectUri(redirectTo)) {
      dispatch({ type: 'SET_REDIRECT_TO', redirectTo });
      sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectTo);
    }
  }, [dispatch, location.search]);

  // On mount, check for an existing Supabase session. If the user returns
  // after completing the magic link callback, skip auth and advance to the
  // appropriate next step based on flow type.
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await getSupabaseClient().auth.getSession();
        if (session) {
          dispatch({ type: 'SET_SESSION', session });
          // Read redirect_to directly from the URL — state.redirectTo isn't populated
          // yet at this point (SET_REDIRECT_TO dispatched in a sibling effect).
          const redirectParam = buildRedirectParam(
            new URLSearchParams(location.search).get('redirect_to'),
          );
          if (state.accountIntent === AccountIntent.Signin) {
            // Active session on the login path — skip auth, go straight to complete.
            dispatch({ type: 'SET_STEP', step: 'activation_complete' });
            history.replace(`${url}/complete${redirectParam}`);
          } else {
            // Create path: verify activate/confirm succeeded before routing to coverage.
            // A valid session only means OTP passed — the entitlement may still be
            // pending if activate/confirm failed mid-flow (e.g. Cortex link error).
            // activateSleepioConfirm is idempotent: returns 200 if already active.
            try {
              await activateSleepioConfirm({ accessToken: session.access_token });
              dispatch({ type: 'SET_STEP', step: 'coverage_form' });
              history.replace(`${url}/coverage${redirectParam}`);
            } catch {
              // Account not fully set up — sign out and restart so the user
              // goes through OTP again, which will retry activate/confirm.
              await getSupabaseClient().auth.signOut();
              history.replace(`${url}/auth${redirectParam}`);
            }
          }
        }
      } finally {
        setSessionChecked(true);
      }
    };
    checkSession();
  }, []); // eslint-disable-line

  if (!sessionChecked) {
    return null;
  }

  const authFallbackUrl = `${url}/auth${buildRedirectParam(
    new URLSearchParams(location.search).get('redirect_to'),
  )}`;

  return (
    <Switch>
      <Route exact path={`${path}/auth`} component={AuthMethodSelection} />
      <Route exact path={`${path}/otp`} component={OtpVerification} />
      {/* CAN-1131: email magic link */}
      <Route exact path={`${path}/email`} component={EmailEntry} />
      <Route exact path={`${path}/sent`} component={EmailSent} />
      {/* CAN-1161: magic link callback (tab hop returns here) */}
      <Route exact path={`${path}/email/callback`} component={EmailCallback} />
      {/* DAYL-1139: email + password */}
      <Route exact path={`${path}/password`} render={() => <NotYetImplemented step="email_password" />} />
      {/* CAN-1134: employer coverage form */}
      <Route exact path={`${path}/coverage`} component={CoverageForm} />
      {/* CAN-1152: confirm and handoff */}
      <Route exact path={`${path}/complete`} component={ActivationComplete} />
      <Redirect to={authFallbackUrl} />
    </Switch>
  );
};

// org_slug and intent (sign-up vs login) come from the route path so state is never null on mount.
// See DAYL-1137 for how Canopy constructs these URLs.
const ActivationRouter: React.FC = () => {
  // eslint-disable-next-line camelcase
  const { org_slug: orgSlug } = useParams<RouteParams>();
  const { pathname } = useLocation();
  const accountIntent = pathname.includes('/sign-up') ? AccountIntent.Create : AccountIntent.Signin;

  return (
    <ActivationProvider initialOrgSlug={orgSlug} initialAccountIntent={accountIntent}>
      <ActivationRouterInner />
    </ActivationProvider>
  );
};

export default ActivationRouter;
